import React, { createContext, useContext, useState, useEffect } from 'react';
import { axiosInstance } from '../../../../config/api';
import { cachedApiCall, CACHE_KEYS } from '../../../../utils/apiCache';

/**
 * AutocompleteProvider Component
 * 
 * Provides autocomplete suggestions from the API endpoint for all medical fields.
 * This component fetches doctor recommendations and makes them available throughout
 * the application via React Context.
 * 
 * API Endpoint: /api/prescription/getDoctorRecc?doctor_id=1
 * 
 * Features:
 * - Fetches suggestions for allergies, diagnosis, advice, complaints, and medicines
 * - Provides real-time filtering based on user input
 * - Supports custom input (allows typing values not in suggestions)
 * - Caches suggestions for better performance
 * - Handles API errors gracefully with fallback data
 */

// Create context for autocomplete suggestions
const AutocompleteContext = createContext();

// Custom hook to use autocomplete context
export const useAutocomplete = () => {
  const context = useContext(AutocompleteContext);
  if (!context) {
    throw new Error('useAutocomplete must be used within an AutocompleteProvider');
  }
  return context;
};

// AutocompleteProvider component
export const AutocompleteProvider = ({ children, doctorId = null }) => {
  // Get doctor ID from localStorage if not provided
  const defaultDoctorId = (() => {
    if (doctorId) return doctorId;
    const tokenPayload = JSON.parse(localStorage.getItem('tokenPayload') || '{}');
    return tokenPayload?.userId || null ;
  })();
  
  const finalDoctorId = defaultDoctorId;
  const [suggestions, setSuggestions] = useState({
    allergies: {
      medicines: [],
      food: [],
      others: []
    },
    diagnosis: [],
    advices: [],
    complaints: [],
    medicines: []
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch suggestions from API
   * Transforms the API response into a structured format for easy consumption
   */
  const fetchSuggestions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🔥 Fetching autocomplete suggestions from API...');
      
      const cacheKey = CACHE_KEYS.DOCTOR_RECOMMENDATIONS(finalDoctorId);

      // Use cachedApiCall for request deduplication and caching
      const responseData = await cachedApiCall(
        cacheKey,
        async () => {
          const response = await axiosInstance.get(`api/prescription/getDoctorRecc?doctor_id=${finalDoctorId}`);
          return response.data;
        },
        { ttl: 5 * 60 * 1000 } // 5 minutes TTL
      );
      
      if (responseData) {
      // console.log('🔥 Raw API response:', responseData);
        
        // Transform API response to structured suggestions
        const transformedSuggestions = {
          // Extract allergy suggestions
          allergies: {
            medicines: responseData.allergies
              ?.map(item => item.medicines)
              .filter(item => item && item.trim() !== '') || [],
            food: responseData.allergies
              ?.map(item => item.food)
              .filter(item => item && item.trim() !== '') || [],
            others: responseData.allergies
              ?.map(item => item.others)
              .filter(item => item && item.trim() !== '') || []
          },
          
          // Extract diagnosis suggestions
          diagnosis: responseData.diagnosis
            ?.map(item => item.diagnosis)
            .filter(item => item && item.trim() !== '') || [],
          
          // Extract advice suggestions
          advices: responseData.advices
            ?.map(item => item.advice_text)
            .filter(item => item && item.trim() !== '') || [],
          
          // Extract complaint suggestions with severity
          complaints: responseData.complaints
            ?.filter(item => item.complaint && item.complaint.trim() !== '')
            .map(item => ({
              complaint: item.complaint,
              severity: item.severity || ''
            })) || [],
          
          // Extract medicine suggestions
          medicines: responseData.medicines
            ?.map(item => item.name)
            .filter(item => item && item.trim() !== '') || []
        };
        
        // console.log('🔥 Transformed suggestions:', transformedSuggestions);
        setSuggestions(transformedSuggestions);
        
        // Cache suggestions in localStorage for offline use
        localStorage.setItem('autocomplete_suggestions', JSON.stringify(transformedSuggestions));
        
      } else {
        console.warn('🔥 No data received from API');
        loadCachedSuggestions();
      }
      
    } catch (error) {
      console.error('🔥 Error fetching suggestions:', error);
      setError('Failed to load suggestions');
      
      // Load cached suggestions as fallback
      loadCachedSuggestions();
      
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load cached suggestions from localStorage
   */
  const loadCachedSuggestions = () => {
    try {
      const cached = localStorage.getItem('autocomplete_suggestions');
      if (cached) {
        const parsedSuggestions = JSON.parse(cached);
        setSuggestions(parsedSuggestions);
        console.log('🔥 Loaded cached suggestions');
      }
    } catch (error) {
      console.error('🔥 Error loading cached suggestions:', error);
    }
  };

  /**
   * Filter suggestions based on input text
   * @param {string} category - Category of suggestions (medicines, diagnosis, etc.)
   * @param {string} subcategory - Subcategory for nested objects (e.g., allergies.medicines)
   * @param {string} input - User input to filter against
   * @returns {Array} Filtered suggestions
   */
  const getFilteredSuggestions = (category, subcategory = null, input = '') => {
    if (!input || input.length < 1) return [];
    
    let targetSuggestions = [];
    
    if (subcategory && suggestions[category] && suggestions[category][subcategory]) {
      targetSuggestions = suggestions[category][subcategory];
    } else if (suggestions[category]) {
      targetSuggestions = suggestions[category];
    }
    
    // Filter suggestions based on input (case-insensitive)
    const filtered = targetSuggestions.filter(item => {
      const searchText = typeof item === 'string' ? item : item.complaint || '';
      return searchText.toLowerCase().includes(input.toLowerCase());
    });
    
    // Limit to 8 suggestions for better UX
    return filtered.slice(0, 8);
  };

  /**
   * Get severity suggestions for complaints
   * @param {string} complaint - Complaint text to find severity for
   * @returns {string} Associated severity or empty string
   */
  const getSeverityForComplaint = (complaint) => {
    const complaintObj = suggestions.complaints.find(
      item => item.complaint && item.complaint.toLowerCase() === complaint.toLowerCase()
    );
    return complaintObj?.severity || '';
  };

  /**
   * Get all unique severity options
   * @returns {Array} Array of unique severity strings
   */
  const getAllSeverityOptions = () => {
    const severities = suggestions.complaints
      .map(item => item.severity)
      .filter(severity => severity && severity.trim() !== '');
    
    // Add default severities and remove duplicates
    const defaultSeverities = ['Mild', 'Moderate', 'Severe', 'High', 'Low'];
    return [...new Set([...severities, ...defaultSeverities])];
  };

  // Fetch suggestions on component mount
  useEffect(() => {
    fetchSuggestions();
    
    // Set up periodic refresh (every 5 minutes)
    const interval = setInterval(fetchSuggestions, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [finalDoctorId]);

  // Context value
  const contextValue = {
    suggestions,
    isLoading,
    error,
    getFilteredSuggestions,
    getSeverityForComplaint,
    getAllSeverityOptions,
    refreshSuggestions: fetchSuggestions
  };

  return (
    <AutocompleteContext.Provider value={contextValue}>
      {children}
    </AutocompleteContext.Provider>
  );
};

export default AutocompleteProvider;